import { SendMessage } from 'react-use-websocket';
import { NavLink, generatePath } from 'react-router-dom';
import {
  headingsPlugin,
  linkPlugin,
  listsPlugin,
  MDXEditor,
} from '@mdxeditor/editor';
import { apiAbortController } from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import { Channel, ChannelType } from '../hoc/types';
import {
  setSelectedChannel,
  setRequestChannels,
  setMainChannels,
} from '../hoc/store/slices/general';
import formatTitle from '../hoc/helpers/format-title';
import formatСhatDate from '../hoc/helpers/format-chat-date';

import cn from './messages.module.css';

interface MessageItemProps {
  message: Channel;
  path: string;
  userId: string;
  sendMessage: SendMessage;
}

const MessageItem = ({
  message,
  path,
  userId,
  sendMessage,
}: MessageItemProps) => {
  const dispatch = useAppDispatch();

  const link = generatePath(path, { messageId: message.id });

  const {
    general: { mainChannels, requestChannels },
  } = useAppSelector((state) => state.messages);

  let dateString = message?.createdAt;

  if (message?.updatedAt) {
    dateString = message?.updatedAt;
  } else if (message?.messages?.edges?.length > 0) {
    dateString = message?.messages?.edges[0].node.createdAt;
  }

  return (
    <NavLink
      to={link}
      className={({ isActive }) =>
        `${cn.messageItem} ${isActive ? cn.messageItemActive : ''} ${
          message.status === 'CLOSED' ? cn.chatClosed : ''
        }`
      }
      onClick={() => {
        dispatch(setSelectedChannel(message));

        apiAbortController.abortPendingRequest();

        sendMessage(
          JSON.stringify({
            event: 'subscribe',
            data: { channelId: message.id },
          }),
        );

        if (message?.channelType !== ChannelType.MAIN) {
          const nextRequestChannels = requestChannels?.map((item) => {
            const result = { ...item };

            if (result.id === message?.id && result.hasUnread) {
              result.hasUnread = false;
            }

            return result;
          });

          dispatch(setRequestChannels(nextRequestChannels as Channel[]));
        } else {
          const nextMainChannels = mainChannels?.map((item) => {
            const result = { ...item };

            if (result.id === message?.id && result.hasUnread) {
              result.hasUnread = false;
            }

            return result;
          });

          dispatch(setMainChannels(nextMainChannels as Channel[]));
        }
      }}
    >
      <div className={cn.messageTop}>
        <div className={cn.messageItemTitle}>
          {message.hasUnread && <div className={cn.unreadHint} />}
          <span className={message.hasUnread ? cn.minWidth : ''}>
            {formatTitle(message)}
          </span>
        </div>
        <div className={cn.messageItemRight}>
          {message.status === 'CLOSED' ? (
            <div className={cn.closedStatus}>Chat Closed</div>
          ) : (
            formatСhatDate(dateString, true)
          )}
        </div>
      </div>
      {message.channelType === ChannelType.TRANSACTION &&
        message.transactionId && (
          <div className={cn.transactionId}>{`Transaction ${
            message.transactionId
          } (${message?.user?.profile?.firstName} ${message?.user?.profile?.lastName})`}</div>
        )}
      {message?.messages?.edges?.length > 0 && (
        <MDXEditor
          className={cn.messageItemText}
          markdown={
            message?.messages?.edges[0].node.content ||
            message?.messages?.edges[0].node?.document?.name ||
            ''
          }
          readOnly
          plugins={[linkPlugin(), listsPlugin(), headingsPlugin()]}
        />
      )}
      {message?.employee && (
        <div className={cn.messagesAssigned}>
          {message?.employeeId === userId
            ? 'Assigned to you'
            : `${message.employee?.profile?.firstName} ${message.employee?.profile?.lastName} (${message?.employee?.email})`}
        </div>
      )}
    </NavLink>
  );
};

export default MessageItem;
